import { ApiErrorMessageWithTrans } from '@components/Error/ApiErrorMessageWithTrans';
import { plural, t } from '@lingui/core/macro';
import { CarRecordListForBooking } from '@navio/nsdk';
import { invalidateQuery, noddiAsync, URLKeys } from 'noddi-async';
import { NoddiLinearProgressLoader } from 'noddi-ui-web';
import { getCarDisplayName } from 'noddi-util';
import { RegNumberSearch } from 'noddi-web';
import { useCurrentBrand } from '../../../stores/BrandStore/brandStore';
import { getCommonTranslations } from '../../../utils/commonTranslations';
import { getErrorCodesWithTrans } from '../../../utils/getErrorCodesWithTranslations';
import { MultiSelectStep } from './MultiSelectStep';

type Props = {
  userGroupId: number;
  cars: CarRecordListForBooking[];
  selectedCarIds: number[];
  onChange: (selectedCarIds: number[]) => void;
};

/**
 * Pick the cars on the account, or add one by its license plate. An added car joins the account right away, so the
 * tire sizes the request saves on its wheel set are there the next time the customer asks.
 */
export const CarStep = ({ userGroupId, cars, selectedCarIds, onChange }: Props) => {
  const { currentBrand, brandEmail } = useCurrentBrand();

  const {
    mutate: addCarToUserGroup,
    isPending: isAddingCar,
    error: addCarError
  } = noddiAsync.usePost({
    type: URLKeys.postAddCarToUserGroup,
    queryConfig: {
      // Returned so the list holds the car before it is selected: the request only covers listed cars.
      onSuccess: () => invalidateQuery({ urlKey: URLKeys.getPaginatedUserGroupCarsForBooking })
    }
  });

  const hasCars = cars.length > 0;

  return (
    <div className='flex flex-col gap-6'>
      {hasCars && (
        <MultiSelectStep
          question={plural(cars.length, {
            one: 'Which car do you want to request an offer for?',
            other: 'Which cars do you want to request an offer for?'
          })}
          options={cars.map((car) => ({ value: car.id, label: getCarDisplayName({ car, showYear: false }) }))}
          selected={selectedCarIds}
          onChange={onChange}
        />
      )}
      <div className='flex flex-col gap-2'>
        <p className='font-semibold'>
          {hasCars
            ? t`Car not on the list? Add it with its license plate`
            : t`Which car do you want to request an offer for?`}
        </p>
        {!hasCars && (
          <p className='text-sm text-muted-foreground'>{t`Enter its license plate and we add it to your account.`}</p>
        )}
        <RegNumberSearch
          // The lookup's car is a registry cache without an owner; the account gets its own car for the plate,
          // or the one it already has, so a plate already on the list just selects that car.
          addCar={(found) =>
            addCarToUserGroup(
              { userGroupId, carIds: [found.id] },
              {
                onSuccess: ({ data }) => {
                  const addedCarId = data[0]?.car.id;
                  if (addedCarId !== undefined && !selectedCarIds.includes(addedCarId)) {
                    onChange([...selectedCarIds, addedCarId]);
                  }
                }
              }
            )
          }
          translations={getCommonTranslations().getRegNumberText()}
          errorCodes={getErrorCodesWithTrans(brandEmail)}
          brandDomain={currentBrand}
        />
        {isAddingCar && <NoddiLinearProgressLoader />}
        {addCarError && <ApiErrorMessageWithTrans error={addCarError} />}
      </div>
    </div>
  );
};
